/**
 * InstructionModal — shown before every battle or chat session.
 * Picks a RANDOM instruction video from the per-language pool on every
 * language selection; falls back to static avatar + ElevenLabs TTS when
 * no video is available for that language (e.g. Bhojpuri).
 *
 * To add more videos: drop them in /public following the pattern
 *   instruction-<male|female>-<english|hindi|bhojpuri>[-N].mp4
 * and add the path to the appropriate INSTRUCTION_VIDEOS pool below.
 *
 * Props:
 *   mode: "battle" | "chat" | "blitz"
 *   onStart: () => void
 *   hostAvatar: string   (optional fallback image)
 */

import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, ChevronRight, Loader2 } from "lucide-react";

// ── Video pools per language (add more files here as they arrive) ────────────
const INSTRUCTION_VIDEOS = {
  en:  [
    "/instruction-female-english-captain-marvel.mp4",
    "/instruction-male-english-captain-America.mp4",
  ],
  hi:  [
    "/instruction-female-hindi-arrow-woman.mp4",
    "/instruction-male-hindi-spiderman.mp4",
    "/instruction-male-hindi-thor.mp4",
  ],
  bho: [], // no Bhojpuri video yet → TTS fallback
};

/** Pick a random video from the pool for this language. Returns null if empty. */
function pickRandomVideo(lang) {
  const pool = INSTRUCTION_VIDEOS[lang] || [];
  if (!pool.length) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

// Fallback static avatars (used only when no video is available)
const ALL_AVATARS = [
  "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png",
  "/35-girl.png", "/36-girl.png", "/38-girl.png",
  "/27-boy.png",  "/28-boy.png",  "/29-boy.png",  "/30-boy.png",
  "/31-boy.png",  "/32-boy.png",  "/33-boy.png",  "/37-boy.png",
];
let avatarCursor = 0;
function pickNextAvatar() {
  const a = ALL_AVATARS[avatarCursor % ALL_AVATARS.length];
  avatarCursor++;
  return a;
}

// ── Instruction content ──────────────────────────────────────────────────────
const CONTENT = {
  battle: {
    en: {
      title: "Battle Rules",
      lines: [
        "Two players — one screen — ten questions each.",
        "Each question is multiple choice (A, B, C, D).",
        "You have 30 seconds to answer each question.",
        "Tap an option or speak your answer aloud.",
        "10 points for a correct answer · 0 for wrong.",
        "The player with the highest total score wins.",
        "Stay focused — the AI Judge is watching! 🤖",
      ],
      cta: "I'm Ready — Let's Battle!",
      audio: "Welcome to Proctorly AI Battle! Here are the rules. Two players, one screen, ten questions each. Each question is multiple choice. You have thirty seconds to answer. Tap an option or speak your answer. Ten points for correct, zero for wrong. The highest score wins. Stay focused — let's begin!",
    },
    hi: {
      title: "बैटल के नियम",
      lines: [
        "दो खिलाड़ी — एक स्क्रीन — दस सवाल प्रत्येक।",
        "हर सवाल बहुविकल्पीय होगा (A, B, C, D)।",
        "हर सवाल का जवाब देने के लिए 30 सेकंड मिलेंगे।",
        "विकल्प टैप करें या जोर से बोलकर जवाब दें।",
        "सही जवाब पर 10 अंक · गलत पर 0 अंक।",
        "सबसे ज़्यादा अंक पाने वाला खिलाड़ी जीतेगा।",
        "ध्यान केंद्रित रखें — AI जज देख रहा है! 🤖",
      ],
      cta: "मैं तैयार हूँ — चलो बैटल करें!",
      audio: "प्रोक्टरली AI बैटल में आपका स्वागत है! यहाँ नियम हैं। दो खिलाड़ी, एक स्क्रीन, दस सवाल। हर सवाल बहुविकल्पीय होगा। जवाब देने के लिए तीस सेकंड मिलेंगे। विकल्प टैप करें या बोलकर जवाब दें। सही जवाब पर दस अंक, गलत पर शून्य। सबसे ज़्यादा अंक जीतेगा। ध्यान रखें — चलिए शुरू करते हैं!",
    },
    bho: {
      title: "बैटल के नियम",
      lines: [
        "दू खिलाड़ी — एक स्क्रीन — दस सवाल हर एक के।",
        "हर सवाल बहुविकल्पीय होई (A, B, C, D)।",
        "हर सवाल के जवाब देबे के 30 सेकंड मिली।",
        "विकल्प दबाईं या जोर से बोल के जवाब दीं।",
        "सही जवाब पर 10 अंक · गलत पर 0 अंक।",
        "सबसे ज़्यादा अंक पाए वाला जीतेगा।",
        "ध्यान राखीं — AI जज देखत बा! 🤖",
      ],
      cta: "हम तैयार बानी — चलीं बैटल करीं!",
      audio: "प्रोक्टरली AI बैटल में रउआ के स्वागत बा! नियम सुनीं। दू खिलाड़ी, एक स्क्रीन, दस सवाल। हर सवाल के जवाब देबे के तीस सेकंड मिली। विकल्प दबाईं या बोल के जवाब दीं। सही जवाब पर दस अंक, गलत पर शून्य। सबसे ज़्यादा अंक जीतेगा। ध्यान राखीं — चलीं शुरू करीं!",
    },
  },
  chat: {
    en: {
      title: "Talking to Kiba",
      lines: [
        "You are about to chat with Kiba — your AI coach.",
        "Ask about careers, technology, AI, or anything on your mind.",
        "Kiba speaks English, Hindi, and Bhojpuri.",
        "Toggle the language at any time during the conversation.",
        "Type your message or speak — Kiba will respond.",
        "No question is too big or too small. She's here for you!",
      ],
      cta: "Start Chatting with Kiba",
      audio: "You are about to meet Kiba, your AI coach from Proctorly. Ask her anything about careers, technology, or life. She speaks English, Hindi and Bhojpuri. Toggle the language anytime. Let's get started!",
    },
    hi: {
      title: "Kiba से बातचीत",
      lines: [
        "आप Kiba से बात करने वाले हैं — आपकी AI कोच।",
        "करियर, टेक्नोलॉजी, AI या कुछ भी पूछें।",
        "Kiba हिंदी, अंग्रेज़ी और भोजपुरी बोलती है।",
        "बातचीत के दौरान कभी भी भाषा बदलें।",
        "टाइप करें या बोलें — Kiba जवाब देगी।",
        "कोई भी सवाल छोटा या बड़ा नहीं होता!",
      ],
      cta: "Kiba से बात शुरू करें",
      audio: "आप Kiba से मिलने वाले हैं — Proctorly AI की आपकी कोच। करियर, टेक्नोलॉजी, या कुछ भी पूछें। वो हिंदी, अंग्रेज़ी और भोजपुरी बोलती है। चलिए शुरू करते हैं!",
    },
    bho: {
      title: "Kiba से बतियाईं",
      lines: [
        "रउआ Kiba से मिले वाले बानी — AI कोच।",
        "करियर, टेक्नोलॉजी, AI — कुछ भी पूछीं।",
        "Kiba हिंदी, अंग्रेज़ी आ भोजपुरी बोलेली।",
        "बतियाते समय कभी भी भाषा बदल सकत बानी।",
        "टाइप करीं या बोलीं — Kiba जवाब देई।",
        "कवनो सवाल छोट ना होत! इ आपके बा।",
      ],
      cta: "Kiba से बतियाना शुरू करीं",
      audio: "रउआ Kiba से मिले वाले बानी — Proctorly AI के कोच। करियर, टेक्नोलॉजी, या कुछ भी पूछीं। चलीं शुरू करीं!",
    },
  },
  blitz: {
    en: {
      title: "Blitz Rules",
      lines: [
        "10 AI-generated multiple choice questions await you.",
        "Each question has options A, B, C, D.",
        "Tap an option or speak your answer aloud.",
        "Answer all 10 to earn your Proctorly certificate.",
        "You can ask the AI to repeat a question anytime.",
        "Fast, fun, and fair — let's see what you know!",
      ],
      cta: "Start Blitz!",
      audio: "Welcome to Proctorly Blitz! Ten multiple choice questions. Tap or speak your answer. Answer all ten to earn your certificate. Let's go!",
    },
    hi: {
      title: "ब्लिट्ज़ के नियम",
      lines: [
        "10 AI-जनित बहुविकल्पीय प्रश्न आपका इंतजार कर रहे हैं।",
        "हर सवाल में विकल्प A, B, C, D होंगे।",
        "विकल्प टैप करें या जोर से बोलकर जवाब दें।",
        "सभी 10 जवाब देने पर Proctorly सर्टिफिकेट मिलेगा।",
        "AI से कभी भी सवाल दोहराने को कह सकते हैं।",
        "तेज़, मज़ेदार और निष्पक्ष — चलिए देखें!",
      ],
      cta: "ब्लिट्ज़ शुरू करें!",
      audio: "प्रोक्टरली ब्लिट्ज़ में स्वागत है! दस बहुविकल्पीय प्रश्न हैं। विकल्प टैप करें या बोलकर जवाब दें। सभी दस जवाब देने पर सर्टिफिकेट मिलेगा। चलिए!",
    },
    bho: {
      title: "ब्लिट्ज़ के नियम",
      lines: [
        "10 AI सवाल रउआ के इंतजार में बाड़े।",
        "हर सवाल में विकल्प A, B, C, D रही।",
        "विकल्प दबाईं या बोल के जवाब दीं।",
        "सब दस जवाब देने पर सर्टिफिकेट मिली।",
        "AI से कभी भी सवाल दोहरावे के कह सकत बानी।",
        "तेज़, मज़ा वाला — चलीं देखीं!",
      ],
      cta: "ब्लिट्ज़ शुरू करीं!",
      audio: "प्रोक्टरली ब्लिट्ज़ में रउआ के स्वागत बा! दस सवाल बाड़े। विकल्प दबाईं या बोल के जवाब दीं। चलीं!",
    },
  },
};

const LANG_OPTIONS = [
  { id: "en", label: "English" },
  { id: "hi", label: "हिंदी"  },
];

// ── Two explicit video lists — English and Hindi ──────────────────────────
// Add more files to either array any time; one will be picked at random.
const ENGLISH_VIDEOS = [
  "/instruction-female-english-captain-marvel.mp4",
  "/instruction-male-english-captain-America.mp4",
];
const HINDI_VIDEOS = [
  "/instruction-female-hindi-arrow-woman.mp4",
  "/instruction-male-hindi-spiderman.mp4",
  "/instruction-male-hindi-thor.mp4",
];

// Pick ONE random video from the correct language array.
// "random" → picks from the combined pool of ALL languages (used for battle
// where language isn't selected yet, so any character can appear).
// Called only on initial mount and when the user switches language —
// never inside a useEffect (that would double-fire in React Strict Mode).
function getVideoForLang(lang) {
  if (lang === "hi") return HINDI_VIDEOS[Math.floor(Math.random() * HINDI_VIDEOS.length)];
  if (lang === "random") {
    const all = [...ENGLISH_VIDEOS, ...HINDI_VIDEOS];
    return all[Math.floor(Math.random() * all.length)];
  }
  return ENGLISH_VIDEOS[Math.floor(Math.random() * ENGLISH_VIDEOS.length)];
}

export default function InstructionModal({ mode = "battle", onStart, hostAvatar, initialLang = "en" }) {
  const [lang, setLang]             = useState(initialLang);
  const [muted, setMuted]           = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [progress, setProgress]     = useState(0);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [countdown, setCountdown]   = useState(null);
  const [avatarImg]                 = useState(() => hostAvatar || pickNextAvatar());

  // Pick one random video matching the selected language on first render.
  const [videoSrc, setVideoSrc] = useState(() => getVideoForLang(initialLang));

  const videoRef    = useRef(null);
  const rafRef        = useRef(null);
  const fallbackTimer = useRef(null);

  const content = CONTENT[mode]?.[lang] || CONTENT.battle.en;

  // ── 15-second fallback: enable Start button regardless of video state ─────
  useEffect(() => {
    fallbackTimer.current = setTimeout(() => setVideoEnded(true), 15000);
    return () => clearTimeout(fallbackTimer.current);
  }, [videoSrc]); // resets whenever language switches to a new video

  // ── Video playback ────────────────────────────────────────────────────────
  // GUARD: uses the DOM element's own .src attribute — NOT a React ref or
  // useEffect flag (both are reset by React Strict Mode's double-invoke).
  //
  // When Strict Mode runs this effect a second time with the same videoSrc,
  // v.src already ends with videoSrc (set in the first run) → we skip.
  // On a real language switch, v.src ends with the OLD path → we proceed.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;

    // v.src is an absolute URL like "http://localhost:3000/instruction-x.mp4?modal=..."
    // videoSrc is a relative path like "/instruction-x.mp4"
    const srcBase = v.src.split("?")[0];
    if (v.src && srcBase.endsWith(videoSrc)) return; // already loaded — skip

    setVideoEnded(false);
    setProgress(0);
    setSoundBlocked(false);
    cancelAnimationFrame(rafRef.current);

    v.src    = `${videoSrc}?modal=${Date.now()}`;
    v.muted  = muted;
    v.load();

    const startRaf = () => {
      cancelAnimationFrame(rafRef.current);
      const tick = () => {
        if (!v || v.ended || v.paused) return;
        if (v.duration > 0)
          setProgress(Math.min(100, (v.currentTime / v.duration) * 100));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    v.play()
      .then(() => startRaf())
      .catch(() => {
        v.muted = true;
        setSoundBlocked(true);
        v.play()
          .then(() => startRaf())
          .catch(() => { setVideoEnded(true); setProgress(100); });
      });

    return () => cancelAnimationFrame(rafRef.current);
  }, [videoSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync muted state to the DOM element without restarting
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
      if (!muted) setSoundBlocked(false);
    }
  }, [muted]);

  // Countdown 3 → 2 → 1 → 0 → fire onStart
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) { onStart?.(); return; }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown, onStart]);

  // ── Mute toggle ───────────────────────────────────────────────────────────
  const handleMuteToggle = () => setMuted((m) => !m);

  // ── Language switch ───────────────────────────────────────────────────────
  const switchLang = (l) => {
    if (l === lang) return;
    setLang(l);
    setVideoSrc(getVideoForLang(l));
  };

  // ── Start button ──────────────────────────────────────────────────────────
  const handleStart = () => {
    if (!videoEnded) return;
    // Stop video and cancel all pending timers/animations
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = ""; // fully release the video
    }
    cancelAnimationFrame(rafRef.current);
    clearTimeout(fallbackTimer.current);
    setCountdown(3);
  };

  // ── Wall-of-Fighters background (same as /join page) ──────────────────────
  const IM_BG_AVATARS = [
    "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png", "/36-girl.png", "/38-girl.png",
    "/27-boy.png",  "/28-boy.png",  "/29-boy.png",  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png", "/37-boy.png",
    "/fe-1.png",    "/fe-2.png",    "/fe-3.png",    "/fe-4.png",
    "/male-1.png",  "/male-2.png",  "/male-3.png",  "/male-4.png",
  ];
  const IM_COLS = [
    { dir: "up",   speed: 22, delay: "0s",   opacity: 0.70, blur: 0, gs: 55, bri: 0.55 },
    { dir: "down", speed: 29, delay: "2.1s", opacity: 0.60, blur: 0, gs: 50, bri: 0.50 },
    { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.45, blur: 0, gs: 48, bri: 0.44 },
    { dir: "down", speed: 33, delay: "1.1s", opacity: 0.30, blur: 1, gs: 42, bri: 0.38 },
    { dir: "up",   speed: 25, delay: "0s",   opacity: 0.20, blur: 3, gs: 38, bri: 0.32 },
    { dir: "down", speed: 20, delay: "1.6s", opacity: 0.20, blur: 3, gs: 38, bri: 0.32 },
    { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.30, blur: 1, gs: 42, bri: 0.38 },
    { dir: "down", speed: 18, delay: "2.6s", opacity: 0.45, blur: 0, gs: 48, bri: 0.44 },
    { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.60, blur: 0, gs: 50, bri: 0.50 },
    { dir: "down", speed: 23, delay: "1.3s", opacity: 0.70, blur: 0, gs: 55, bri: 0.55 },
  ];
  const getImCol = (ci) => {
    const imgs = [];
    for (let i = 0; i < 4; i++) imgs.push(IM_BG_AVATARS[(ci * 3 + i) % IM_BG_AVATARS.length]);
    return [...imgs, ...imgs];
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4"
      style={{ background: "rgba(6,10,31,0.97)", backdropFilter: "blur(8px)" }}
    >
      {/* keyframes used inside the left video panel */}
      <style>{`
        @keyframes im-up   { 0%  { transform: translateY(0);    } 100% { transform: translateY(-50%); } }
        @keyframes im-down { 0%  { transform: translateY(-50%); } 100% { transform: translateY(0);    } }
        @keyframes im-ambi { 0%,100%{opacity:0.14} 50%{opacity:0.30} }
      `}</style>
      {/* ── Countdown overlay: 3 → 2 → 1 ─────────────────────────────────── */}
      {countdown !== null && countdown > 0 && (
        <div className="absolute inset-0 z-[60] flex flex-col items-center justify-center"
          style={{ background: "rgba(6,10,31,0.96)" }}>
          <div
            key={countdown}
            className="font-display text-[120px] md:text-[180px] leading-none select-none"
            style={{
              color: "var(--kl-red)",
              textShadow: "0 0 60px rgba(230,57,70,0.7)",
              animation: "kl-pop 0.5s ease-out",
            }}
          >
            {countdown}
          </div>
          <div className="font-mono-kl text-sm uppercase tracking-[0.35em] text-white/50 mt-4">
            Get Ready…
          </div>
          <style>{`@keyframes kl-pop { 0%{transform:scale(1.6);opacity:0} 60%{transform:scale(0.95);opacity:1} 100%{transform:scale(1);opacity:1} }`}</style>
        </div>
      )}

      <div
        className="relative w-full rounded-2xl overflow-hidden flex flex-col md:flex-row"
        style={{
          background: "var(--kl-navy-deep)",
          border: "1.5px solid rgba(230,57,70,0.50)",
          boxShadow: "0 0 100px rgba(230,57,70,0.30), 0 40px 100px rgba(0,0,0,0.85)",
          height: "calc(100vh - 32px)",
          maxHeight: "calc(100vh - 32px)",
          zIndex: 10,
          position: "relative",
        }}
      >
        {/* ── Left: Agent video / avatar — intentionally large ─────────── */}
        <div
          className="relative flex-shrink-0 overflow-hidden"
          style={{ width: "clamp(300px, 52%, 680px)", height: "100%", background: "var(--kl-navy-deep)" }}
        >
          {/* ── Wall-of-Fighters animated background inside the video panel ── */}
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
            {IM_COLS.map((col, ci) => (
              <div key={ci} style={{
                position: "absolute",
                left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
                overflow: "hidden",
                opacity: col.opacity,
                filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
              }}>
                <div style={{
                  display: "flex", flexDirection: "column", gap: 6,
                  animation: `${col.dir === "up" ? "im-up" : "im-down"} ${col.speed}s linear ${col.delay} infinite`,
                }}>
                  {getImCol(ci).map((src, ii) => (
                    <img key={ii} src={src} alt="" style={{
                      width: "100%", height: "27vh",
                      objectFit: "cover", objectPosition: "top center",
                      borderRadius: 8, flexShrink: 0,
                      filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.2)`,
                    }} />
                  ))}
                </div>
              </div>
            ))}
            {/* Side fades */}
            <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(6,10,31,0.80) 0%, transparent 20%, transparent 80%, rgba(6,10,31,0.80) 100%)" }} />
            {/* Top/bottom fades */}
            <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(6,10,31,0.80) 0%, transparent 18%, transparent 82%, rgba(6,10,31,0.80) 100%)" }} />
            {/* Red arena glow */}
            <div style={{ position: "absolute", top: "10%", left: "50%", transform: "translateX(-50%)", width: "120%", height: "80%", borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.18) 0%, transparent 65%)", animation: "im-ambi 3.5s ease-in-out infinite", zIndex: 2 }} />
            {/* Cyber grid */}
            <div style={{ position: "absolute", inset: 0, opacity: 0.04, zIndex: 2, backgroundImage: "linear-gradient(rgba(230,57,70,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(230,57,70,0.9) 1px, transparent 1px)", backgroundSize: "55px 55px" }} />
          </div>

          {videoSrc ? (
            /* mix-blend-mode:screen makes black video pixels transparent so the
               animated fighters columns show through around/behind the character */
            <video
              ref={videoRef}
              playsInline
              onEnded={() => { cancelAnimationFrame(rafRef.current); setVideoEnded(true); setProgress(100); }}
              onError={() => { cancelAnimationFrame(rafRef.current); setVideoEnded(true); setProgress(100); }}
              className="w-full h-full object-cover object-top"
              style={{ position: "relative", zIndex: 3, mixBlendMode: "screen" }}
            />
          ) : (
            <img
              src={avatarImg} alt="Host"
              className="w-full h-full object-cover object-top"
              style={{ position: "relative", zIndex: 3, mixBlendMode: "screen" }}
              onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
            />
          )}

          {/* Progress bar at the bottom of the video panel */}
          <div className="absolute inset-x-0 bottom-0 h-1 z-20" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div
              className="h-full transition-none"
              style={{
                width: `${progress}%`,
                background: videoEnded ? "#22c55e" : "var(--kl-red)",
                transition: "width 0.1s linear",
              }}
            />
          </div>

          {/* Status badge */}
          <div className="absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}>
            {videoEnded ? (
              <><span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="font-mono-kl text-[9px] uppercase tracking-widest text-green-300">Ready</span></>
            ) : muted ? (
              <><VolumeX className="w-3 h-3 text-white/40" />
                <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">Muted</span></>
            ) : (
              <><Volume2 className="w-3 h-3 text-red-400 animate-pulse" />
                <span className="font-mono-kl text-[9px] uppercase tracking-widest text-red-300">Playing…</span></>
            )}
          </div>

          {/* Sound-blocked banner — tap to unmute */}
          {soundBlocked && !muted && (
            <button
              onClick={() => { setMuted(false); setSoundBlocked(false); }}
              className="absolute bottom-16 inset-x-4 z-20 flex items-center justify-center gap-2 py-3 rounded-xl font-mono-kl text-sm uppercase tracking-widest text-white transition-all hover:scale-[1.02]"
              style={{ background: "rgba(230,57,70,0.88)", backdropFilter: "blur(6px)" }}
            >
              <Volume2 className="w-4 h-4" /> Tap to enable sound 🔊
            </button>
          )}

          <div className="absolute inset-x-0 bottom-1 h-16 pointer-events-none md:hidden"
            style={{ background: "linear-gradient(to top, var(--kl-navy-deep), transparent)" }} />
        </div>

        {/* ── Right: Instructions ───────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-10 md:px-14 py-10 md:py-12">

          {/* Top row */}
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
              Proctorly AI · Instructions
            </div>
            <button
              onClick={handleMuteToggle}
              className="flex items-center gap-2 font-mono-kl text-xs uppercase tracking-widest text-white/40 hover:text-white transition"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              {muted ? "Unmute" : "Mute"}
            </button>
          </div>

          {/* Language tabs */}
          <div className="flex gap-1.5 mb-8 p-1.5 rounded-xl w-fit" style={{ background: "rgba(255,255,255,0.05)" }}>
            {LANG_OPTIONS.map((l) => (
              <button
                key={l.id}
                onClick={() => switchLang(l.id)}
                className="px-6 py-2 rounded-lg font-mono-kl text-sm uppercase tracking-widest transition-all"
                style={{
                  background: lang === l.id ? "var(--kl-red)" : "transparent",
                  color:      lang === l.id ? "#fff"          : "rgba(255,255,255,0.45)",
                  fontWeight: lang === l.id ? "700"           : "400",
                }}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Title */}
          <h2 className="font-display text-5xl md:text-6xl uppercase text-white leading-tight mb-8">
            {content.title}
          </h2>

          {/* Rules */}
          <ul className="flex flex-col gap-5 mb-10">
            {content.lines.map((line, i) => (
              <li key={i} className="flex items-start gap-4">
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-mono-kl text-sm font-bold mt-0.5"
                  style={{ background: "rgba(230,57,70,0.18)", color: "var(--kl-red)", border: "1px solid rgba(230,57,70,0.40)" }}
                >
                  {i + 1}
                </span>
                <span className="text-white/85 text-lg md:text-xl leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>

          {/* CTA — locked until the single video finishes */}
          <div className="mt-auto">
            {!videoEnded && (
              <p className="text-center font-mono-kl text-xs uppercase tracking-widest text-white/30 mb-4">
                Please watch the instruction video ↓
              </p>
            )}
            <button
              onClick={handleStart}
              disabled={!videoEnded || countdown !== null}
              className="w-full flex items-center justify-center gap-3 py-6 rounded-2xl font-display text-3xl uppercase tracking-wide text-white transition-all"
              style={{
                background: videoEnded
                  ? "linear-gradient(135deg, #e63946, #c1121f)"
                  : "rgba(255,255,255,0.06)",
                boxShadow: videoEnded ? "0 0 30px rgba(230,57,70,0.50), 0 4px 20px rgba(0,0,0,0.4)" : "none",
                cursor: videoEnded ? "pointer" : "not-allowed",
                opacity: videoEnded ? 1 : 0.45,
              }}
            >
              {videoEnded ? (
                <>{content.cta} <ChevronRight className="w-6 h-6" /></>
              ) : (
                <><Loader2 className="w-5 h-5 animate-spin" /> Video playing…</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
